import { useState, useEffect } from "react";
import DashboardLayout from "../../Layout/DashboardLayout";
import PageHeader from "../../Common/PageHeader";
import KPICards from "./ProjectManagerDashboard/KPICards";
import LabKPICards from "./LabManagerDashboard/KPICards";
import TestEngineerKPICards from "./TestEngineerDashboard/KPICards";
import ProjectWiseHours from "./ProjectManagerDashboard/ProjectWiseHours";
import TestTypeAnalysis from "./ProjectManagerDashboard/TestTypeAnalysis";
import LabWiseProject from "./ProjectManagerDashboard/LabWiseProject";
import LabWiseTestTypeAnalysis from "./ProjectManagerDashboard/LabWiseTestTypeAnalysis";
import HappinessIndexDashboard from "./HappinessIndex/HappinessIndexDashboard";
import LabOccupancyHeatmap from "./LabManagerDashboard/LabOccupancyHeatmap";
import LabUtilizationRanking from "./LabManagerDashboard/LabUtilizationRanking";
import BookingPipelineChart from "./LabManagerDashboard/BookingPipeline";
import { companyID, DashboardCard } from "../../Common/UIComponents";
import ResourceUtilization from "./TestEngineerDashboard/ProjectResourseUtilization";
import MPDashboardIndex from "./MPDashboard/MPDashboardIndex";

const DashboardMain = () => {
  // Role code, kept as state (not a plain const) so a role switch in the
  // sidebar actually re-renders this dashboard instead of only updating
  // localStorage silently. Sidebar.js already dispatches "roleChanged" on
  // every switch (its own menu listens for the same event) — this just
  // listens for it too, instead of needing a full page reload to notice.
  const [roleCode, setRoleCode] = useState(
    () => localStorage.getItem("roleCode") || "",
  );

  useEffect(() => {
    const onRoleChanged = () => {
      setRoleCode(localStorage.getItem("roleCode") || "");
    };
    window.addEventListener("roleChanged", onRoleChanged);
    return () => window.removeEventListener("roleChanged", onRoleChanged);
  }, []);

  const [selectedProjectID, setSelectedProjectID] = useState(0);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [projectWiseChartHeight, setProjectWiseChartHeight] = useState(400);
  const [selectedFacilityID, setSelectedFacilityID] = useState(0);
  const [facilityList, setFacilityList] = useState([]);
  const [projectList, setProjectList] = useState([]);

  // Check if user is Project Manager
  const isProjectManager = roleCode === "PM";
  const isLabManager = roleCode === "LM";
  const isTestEngineer = roleCode === "TO";
  const isAdmin = roleCode === "AD";
  const isProjectResponsible = roleCode === "PR";

  // Show access denied if no valid role
  if (!isProjectManager && !isLabManager && !isTestEngineer && !isAdmin) {
    return (
      <DashboardLayout>
        <div className="p-4 bg-[#F3F2F1] min-h-screen flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-slate-200/80 max-w-sm">
            <span className="material-symbols-outlined text-5xl text-red-500 mb-4 block">
              lock
            </span>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Access Denied
            </h2>
            <p className="text-slate-500 text-sm">
              You don't have permission to view the dashboard.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              Current Role: {roleCode || "Unknown"}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 bg-[#F3F2F1] min-h-screen">
        {/* <PageHeader
          title={
            isAdmin
              ? "Admin Dashboard"
              : isProjectManager
                ? "Project Manager Dashboard"
                : isLabManager
                  ? "Lab Manager Dashboard"
                  : "Test Engineer Dashboard"
          }
        /> */}

        <div className="space-y-3">
          {/* ══════════════════════════════════════════════════════════════ */}
          {/* Happiness Index — the component renders its own DashboardCard
              header (with its date-picker/tabs actions), so it isn't
              wrapped again here. */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <HappinessIndexDashboard />
          {/* ════════════════════════════════════════════════════════════════ */}
          {/* PROJECT MANAGER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {(isProjectManager || isAdmin) && (
            <>
              {/* Section 1: KPI Cards */}
              <DashboardCard icon="speed" title="Project Details">
                <KPICards />
              </DashboardCard>

              {/* Row 1: Project-wise Hours Consumption + Test Type Analysis */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ProjectWiseHours
                  onProjectSelected={(projectID, projectName) => {
                    console.log(
                      "🔥 DashboardMain received - ProjectID:",
                      projectID,
                      "ProjectName:",
                      projectName,
                    );
                    setSelectedProjectID(projectID);
                    setSelectedProjectName(projectName || "");
                    console.log(
                      "✅ State set - ProjectID:",
                      projectID,
                      "Name:",
                      projectName,
                    );
                  }}
                  onProjectListLoaded={setProjectList}
                  selectedProjectID={selectedProjectID}
                  onChartHeightChange={setProjectWiseChartHeight}
                />

                <TestTypeAnalysis
                  selectedProjectID={selectedProjectID}
                  selectedProjectName={selectedProjectName}
                  minHeight={projectWiseChartHeight}
                  projectListProp={projectList}
                  onClearFilter={() => {
                    setSelectedProjectID(0);
                    setSelectedProjectName("");
                  }}
                  onProjectSelected={(projectID, projectName) => {
                    setSelectedProjectID(projectID);
                    setSelectedProjectName(projectName || "");
                  }}
                />
              </div>

              {/* Row 2: Lab-wise Project Utilization alone, full width,
                  until a lab is picked in it — then Lab-wise Test Type
                  Analysis joins it in the same row, two columns. */}
              <div
                className={
                  selectedFacilityID > 0
                    ? "grid grid-cols-1 lg:grid-cols-2 gap-4"
                    : ""
                }
              >
                <LabWiseProject
                  selectedFacilityID={selectedFacilityID}
                  onFacilitySelected={setSelectedFacilityID}
                  onFacilityListLoaded={setFacilityList}
                  onClearSelection={() => setSelectedFacilityID(0)}
                />

                {selectedFacilityID > 0 && (
                  <LabWiseTestTypeAnalysis
                    key={`test-type-${selectedFacilityID}`}
                    selectedFacilityID={selectedFacilityID}
                    facilityList={facilityList}
                  />
                )}
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* LAB MANAGER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {(isLabManager || isAdmin) && (
            <>
              {/* Section 1: KPI Cards */}
              <DashboardCard icon="speed" title="Lab Details">
                <LabKPICards />
              </DashboardCard>

              {/* Section 2: Lab Occupancy Heatmap */}
              {/* <LabOccupancyHeatmap /> */}

              {/* Section 3 & 4: Lab charts side by side, same row-based density as the PM dashboard */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <LabUtilizationRanking />
                {/* BookingPipelineChart renders its own DashboardCard header. */}
                <BookingPipelineChart companyId={companyID} />
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* TEST ENGINEER DASHBOARD */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {isTestEngineer && (
            <>
              {/* Section 1: KPI Cards */}
              <DashboardCard icon="speed" title="Key Performance Indicators">
                <TestEngineerKPICards />
              </DashboardCard>

              {/* ResourceUtilization renders its own DashboardCard header. */}
              <ResourceUtilization />
            </>
          )}
          {(isProjectResponsible || isAdmin) && (
            <DashboardCard icon="fact_check" title="Measurement Protocol Approval Request">
              <MPDashboardIndex />
            </DashboardCard>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardMain;
